import store from "@/store";
import router from "@/router";
import { $api } from "@/services/api";
import type { ApplicantRequest } from "@/models/api";
import { ref } from "vue";

interface PayloadOptions {
  returnUser: boolean;
}

export interface OidcUser {
  access_token: string;
  expires_at: number;
  id_token: string;
  profile: OidcUserProfile;
  scope: string;
  session_state: string;
  token_type: string;
}

export interface OidcUserProfile {
  amr: string[];
  auth_time: number;
  birthdate: Date;
  family_name: string;
  given_name: string;
  idp: string;
  name: string;
  nickname: string;
  phone_number?: string;
  preferred_username: string;
  s_hash: string;
  sid: string;
  sub: string;
}

export default function useAuthentication() {
  const trackAuthenticatedUsers = ref<Record<number, string>>({});
  const trackUserKey = "trackAuthenticatedUsers";

  function setTrackAuthenticatedUser(applicantId: number, userId: string) {
    const rehydrateTrackUsers = localStorage.getItem(trackUserKey);

    if (rehydrateTrackUsers) {
      const parsedTrackUsers: Record<number, string> = JSON.parse(rehydrateTrackUsers);
      trackAuthenticatedUsers.value = {
        ...trackAuthenticatedUsers.value,
        ...parsedTrackUsers,
      };
    }

    trackAuthenticatedUsers.value[applicantId] = userId;

    const jsonString = JSON.stringify(trackAuthenticatedUsers.value);
    localStorage.setItem(trackUserKey, jsonString);
  }

  async function promptLoginCheck(payload: ApplicantRequest) {
    const response = await $api.person.promptLogin(payload);

    return response;
  }

  function getUserIdByApplicantId(applicantId: number): string | undefined {
    const userId = trackAuthenticatedUsers.value[applicantId];

    if (userId !== undefined) {
      return userId;
    }

    const sessionTrackUsers = localStorage.getItem(trackUserKey);

    if (sessionTrackUsers) {
      const parsedTrackUsers: Record<number, string> = JSON.parse(sessionTrackUsers);

      trackAuthenticatedUsers.value = {
        ...trackAuthenticatedUsers.value,
        ...parsedTrackUsers,
      };

      return parsedTrackUsers[applicantId];
    }

    return undefined;
  }

  async function getOidcUser() {
    let oidcUser = store.getters["oidcStore/oidcUser"];

    if (oidcUser === null) {
      oidcUser = await store.dispatch("oidcStore/getOidcUser");
    }

    return oidcUser;
  }

  async function handleLoginRedirect() {
    const user = await getOidcUser();
    if (user) {
      await handleSilentAuthentication(user);
    } else {
      if (!store.getters["oidcStore/oidcUser"] && !store.getters["oidcStore/oidcIsAuthenticated"]) {
        await store
          .dispatch("oidcStore/authenticateOidc")
          .then(() => {})
          .catch(() => console.log("Error authenticateOidc"));
      }
    }
  }

  async function handleLoginRedirectWithParams(options?: any) {
    const user = await getOidcUser();
    if (user) {
      await handleSilentAuthentication(user);
    } else {
      if (!store.getters["oidcStore/oidcUser"] && !store.getters["oidcStore/oidcIsAuthenticated"]) {
        const payload = {
          params: {
            // This will be added to the authorization request URL
            extraQueryParams: {
              applicantId: options?.applicantId,
              prompt: "login", // Force fresh login, bypass cached identity server session
            },
          },
        };

        await store
          .dispatch("oidcStore/authenticateOidc", payload)
          .then(() => {})
          .catch(() => console.log("Error authenticateOidc"));
      }
    }
  }

  async function handleSilentAuthentication(oidcUser: any, options?: PayloadOptions) {
    const user = oidcUser ?? (await getOidcUser());

    if (user) {
      if (!store.getters["oidcStore/oidcIsAuthenticated"]) {
        if (user.expired) {
          await store.dispatch("oidcStore/authenticateOidcSilent");
        }

        store.commit("oidcStore/setOidcAuth", user);
      }
    }

    if (options?.returnUser) {
      return user;
    }
  }

  async function handleGracefulSilentSignOut(currentApplicantId: number) {
    //if user not authenticated return..
    if (!store.getters["oidcStore/oidcIsAuthenticated"]) {
      return;
    }

    const userId = getUserIdByApplicantId(currentApplicantId);

    const isUserAlreadyTracked = userId !== undefined && userId !== "";

    //Do not log out if is solo or cwbr led so the verbiage on the submit page will display the "authenticated" message
    if (!isUserAlreadyTracked) {
      await handleSilentSignOut();
    }
  }

  async function handleSilentSignOut() {
    const user = await getOidcUser();

    if (user) {
      // Call backend API to invalidate session
      try {
        await $api.account.signout(user.sub);
      } catch (err) {
        console.log("Error calling signout API", err);
      }

      await store.dispatch("oidcStore/signOutOidcSilent");

      store
        .dispatch("oidcStore/removeUser")
        .then(() => {
          console.log("removeUser");
        })
        .catch(err => {
          console.log("REMOVE USER ERROR", err);
        });

      store
        .dispatch("oidcStore/removeOidcUser")
        .then(() => {
          console.log("removeOidcUser");
        })
        .catch(err => {
          console.log("REMOVE OIDC USER ERROR", err);
        });
    }
  }

  async function handleSignOut() {
    const user = await getOidcUser();

    if (user) {
      await store.dispatch("oidcStore/signOutOidc");

      store
        .dispatch("oidcStore/removeOidcUser")
        .then(() => {
          router.push("/application");
        })
        .catch(err => {
          console.log("REMOVE OIDC USER ERROR", err);
        });
    }
  }

  return {
    promptLoginCheck,
    handleLoginRedirect,
    handleLoginRedirectWithParams,
    handleSilentAuthentication,
    handleSilentSignOut,
    handleGracefulSilentSignOut,
    handleSignOut,
    setTrackAuthenticatedUser,
    getUserIdByApplicantId,
  };
}
